import { ApolloCache, useSubscription } from '@apollo/client';
import { useEffect, useMemo, useRef } from 'react';

import { useEnhancedMutation, useEnhancedQuery } from '@/modules/common/application';
import {
  GetConversationsQuery,
  GetMessagesQuery,
  ChatPermissionMode,
  ChatReasoningEffort,
  MessageTextDeltaStreamDocument,
  MessageTextDeltaStreamSubscription,
  MessageStatus,
  MessageStreamDocument,
  RespondToChatToolApprovalMutation,
  RespondToChatToolApprovalMutationVariables,
  SendMessageFeedbackMutation,
  SendMessageFeedbackMutationVariables,
  SendMessageMutation,
  SendMessageMutationVariables,
  SenderType,
  ToolType,
} from '@/modules/common/types';

import { GET_CONVERSATIONS } from '../apis/conversation.query';
import {
  RESPOND_TO_CHAT_TOOL_APPROVAL,
  SEND_MESSAGE,
  SEND_MESSAGE_FEEDBACK,
} from '../apis/message.mutation';
import { GET_MESSAGES } from '../apis/message.query';
import { SendMessageConversation } from '../types/conversation';
import type { ChatRequestContext } from '../types/request-context';

interface UseMessageProps {
  conversation: SendMessageConversation | null;
  setConversation: null | ((conversation: SendMessageConversation | null) => void);
  initialMessages?: InitialMessage[] | null;
}

type ConversationListItem = NonNullable<GetConversationsQuery['conversations']>[number];
type ConversationPreviewMessage = NonNullable<ConversationListItem['lastMessage']>;
type CachedMessage = NonNullable<GetMessagesQuery['messages']>[number];
type InitialMessage = NonNullable<SendMessageMutation['sendMessage']['messages']>[number];
type ChatMessageTextDelta = MessageTextDeltaStreamSubscription['messageTextDeltaStream'];
type StreamedMessageText = { content: string; sequence: number; epoch: number };

export type SendChatMessageInput = {
  conversationId?: string | null;
  content: string;
  tool: ToolType;
  projectId?: string | null;
  reasoningEffort?: ChatReasoningEffort | null;
  permissionMode?: ChatPermissionMode | null;
  requestContext?: ChatRequestContext | null;
  attachmentIds?: string[] | null;
};

interface CacheAccess {
  readQuery: ApolloCache<unknown>['readQuery'];
  writeQuery: ApolloCache<unknown>['writeQuery'];
}

function toCachedMessage(
  message: Partial<CachedMessage> & Pick<CachedMessage, 'id'>
): CachedMessage {
  return {
    attachments: [],
    feedback: null,
    toolCalls: [],
    processSteps: [],
    ...message,
  } as CachedMessage;
}

export function mergeMessages(
  existing: CachedMessage[],
  incoming: CachedMessage[]
): CachedMessage[] {
  const merged = new Map(existing.map((message) => [message.id, message]));

  for (const message of incoming) {
    const existingMessage = merged.get(message.id);

    merged.set(message.id, {
      ...existingMessage,
      ...message,
      createdAt: existingMessage?.createdAt ?? message.createdAt,
      attachments: message.attachments ?? existingMessage?.attachments ?? [],
      feedback: message.feedback ?? existingMessage?.feedback ?? null,
      toolCalls: message.toolCalls ?? existingMessage?.toolCalls ?? [],
      processSteps: message.processSteps ?? existingMessage?.processSteps ?? [],
    });
  }

  return [...merged.values()];
}

export function getConversationPreviewMessage(messages: CachedMessage[]): CachedMessage | null {
  return [...messages].reverse().find((message) => message.content.trim().length > 0) ?? null;
}

function upsertConversation(
  conversations: ConversationListItem[],
  conversation: Partial<ConversationListItem> & Pick<ConversationListItem, 'id'>,
  lastMessage?: CachedMessage | ConversationPreviewMessage | null
): ConversationListItem[] {
  const existingConversation = conversations.find((item) => item.id === conversation.id);
  const previewMessage = lastMessage ?? existingConversation?.lastMessage ?? null;
  const nextConversation: ConversationListItem = {
    __typename: 'Conversation',
    id: conversation.id,
    title: conversation.title ?? existingConversation?.title ?? null,
    updatedAt:
      conversation.updatedAt ??
      existingConversation?.updatedAt ??
      previewMessage?.createdAt ??
      new Date().toISOString(),
    isCompleted: conversation.isCompleted ?? existingConversation?.isCompleted ?? false,
    lastMessage: previewMessage,
  };

  if (!existingConversation) {
    return [nextConversation, ...conversations];
  }

  return conversations.map((item) => (item.id === conversation.id ? nextConversation : item));
}

function writeMessages(
  cache: CacheAccess,
  nextConversationId: string,
  incomingMessages: CachedMessage[]
) {
  const existingMessages = cache.readQuery<GetMessagesQuery>({
    query: GET_MESSAGES,
    variables: { conversationId: nextConversationId },
  });

  cache.writeQuery<GetMessagesQuery>({
    query: GET_MESSAGES,
    variables: { conversationId: nextConversationId },
    data: {
      messages: mergeMessages(existingMessages?.messages ?? [], incomingMessages),
    },
  });
}

function writeConversationPreview(
  cache: CacheAccess,
  nextConversation: Partial<ConversationListItem> & Pick<ConversationListItem, 'id'>,
  lastMessage?: CachedMessage | ConversationPreviewMessage | null
) {
  const cachedConversations = cache.readQuery<GetConversationsQuery>({
    query: GET_CONVERSATIONS,
  });

  cache.writeQuery<GetConversationsQuery>({
    query: GET_CONVERSATIONS,
    data: {
      conversations: upsertConversation(
        cachedConversations?.conversations ?? [],
        nextConversation,
        lastMessage
      ),
    },
  });
}

function applyTrackedStreamingContent(
  message: CachedMessage,
  streamedTextByMessageId: Map<string, StreamedMessageText>
) {
  const trackedText = streamedTextByMessageId.get(message.id);

  if (
    !trackedText ||
    message.status !== MessageStatus.IN_PROGRESS ||
    trackedText.content.length <= message.content.length
  ) {
    return message;
  }

  return {
    ...message,
    content: trackedText.content,
  };
}

function writeMessageTextDelta({
  cache,
  conversationId,
  delta,
  streamedTextByMessageId,
}: {
  cache: CacheAccess;
  conversationId: string;
  delta: ChatMessageTextDelta;
  streamedTextByMessageId: Map<string, StreamedMessageText>;
}) {
  const trackedText = streamedTextByMessageId.get(delta.messageId);
  const existingMessages = cache.readQuery<GetMessagesQuery>({
    query: GET_MESSAGES,
    variables: { conversationId },
  });
  const existingMessage = (existingMessages?.messages ?? []).find(
    (message) => message.id === delta.messageId
  );

  if (!existingMessage || existingMessage.status === MessageStatus.COMPLETED) {
    return;
  }

  if (trackedText && delta.epoch < trackedText.epoch) {
    return;
  }

  const isTrackedEpoch = trackedText?.epoch === delta.epoch;
  const expectedSequence = (isTrackedEpoch ? trackedText.sequence : 0) + 1;
  const baseContent = isTrackedEpoch ? trackedText.content : (existingMessage.content ?? '');
  const nextContent = `${baseContent}${delta.delta}`;
  const matchesCurrentSnapshot = nextContent.length === delta.contentLength;
  const isExpectedSequence = delta.sequence === expectedSequence;
  const trackedEpoch = trackedText?.epoch ?? 0;
  const canStartFromCurrentSnapshot =
    matchesCurrentSnapshot &&
    (trackedText?.sequence === 0 ||
      (!isTrackedEpoch &&
        ((existingMessage.content ?? '').length > 0 || delta.epoch > trackedEpoch)));

  if (!matchesCurrentSnapshot) {
    return;
  }

  if (!isExpectedSequence && !canStartFromCurrentSnapshot) {
    return;
  }

  const nextMessage = toCachedMessage({
    ...existingMessage,
    content: nextContent,
    status: MessageStatus.IN_PROGRESS,
  });
  streamedTextByMessageId.set(delta.messageId, {
    content: nextContent,
    sequence: delta.sequence,
    epoch: delta.epoch,
  });

  writeMessages(cache, conversationId, [nextMessage]);
  writeConversationPreview(cache, { id: conversationId }, nextMessage);
}

export const useMessage = ({
  conversation,
  setConversation,
  initialMessages = null,
}: UseMessageProps) => {
  const conversationId = conversation?.id ?? null;
  const streamedTextByMessageIdRef = useRef(new Map<string, StreamedMessageText>());

  const { data, loading, refetch } = useEnhancedQuery<GetMessagesQuery>(GET_MESSAGES, {
    variables: { conversationId },
    skip: !conversationId,
  });

  const [sendMessage, { loading: sendMessageLoading }] = useEnhancedMutation<
    SendMessageMutation,
    SendMessageMutationVariables
  >(SEND_MESSAGE, {
    update(cache, { data: mutationData }) {
      if (!mutationData?.sendMessage) {
        return;
      }

      const nextConversationId = mutationData.sendMessage.conversation.id;
      const nextMessages = mutationData.sendMessage.messages.map((message) =>
        toCachedMessage(message)
      );
      const previewMessage = getConversationPreviewMessage(nextMessages);

      writeMessages(cache, nextConversationId, nextMessages);
      writeConversationPreview(cache, mutationData.sendMessage.conversation, previewMessage);

      if (setConversation) {
        setConversation(mutationData.sendMessage.conversation);
      }
    },
  });

  const [sendMessageFeedback, { loading: sendMessageFeedbackLoading }] = useEnhancedMutation<
    SendMessageFeedbackMutation,
    SendMessageFeedbackMutationVariables
  >(SEND_MESSAGE_FEEDBACK, {
    update(cache, { data: mutationData }) {
      if (!mutationData?.sendMessageFeedback || !conversationId) {
        return;
      }

      const cachedMessages = cache.readQuery<GetMessagesQuery>({
        query: GET_MESSAGES,
        variables: { conversationId },
      });
      if (!cachedMessages?.messages) {
        return;
      }

      cache.writeQuery<GetMessagesQuery>({
        query: GET_MESSAGES,
        variables: { conversationId },
        data: {
          messages: cachedMessages.messages.map((message) =>
            message.id === mutationData.sendMessageFeedback.messageId
              ? {
                  ...message,
                  feedback: mutationData.sendMessageFeedback,
                }
              : message
          ),
        },
      });
    },
  });

  const [respondToChatToolApproval, { loading: respondToChatToolApprovalLoading }] =
    useEnhancedMutation<
      RespondToChatToolApprovalMutation,
      RespondToChatToolApprovalMutationVariables
    >(RESPOND_TO_CHAT_TOOL_APPROVAL, {
      update(cache, { data: mutationData }) {
        const updatedMessage = mutationData?.respondToChatToolApproval;
        if (!updatedMessage || !conversationId) {
          return;
        }

        writeMessages(cache, conversationId, [toCachedMessage(updatedMessage)]);
        writeConversationPreview(cache, { id: conversationId }, toCachedMessage(updatedMessage));
      },
    });

  useSubscription(MessageStreamDocument, {
    variables: { conversationId: conversationId ?? '' },
    onData: ({ client, data: subscription }) => {
      if (!subscription.data || subscription.loading || subscription.error || !conversationId) {
        return;
      }

      const streamedMessage = subscription.data.messageStream;

      if (!streamedMessage) {
        return;
      }

      let nextMessage = toCachedMessage(streamedMessage);
      const trackedText = streamedTextByMessageIdRef.current.get(nextMessage.id);

      if (nextMessage.status === MessageStatus.COMPLETED) {
        streamedTextByMessageIdRef.current.delete(nextMessage.id);
      } else if (trackedText) {
        const hasStructuralState =
          nextMessage.toolCalls.length > 0 || nextMessage.processSteps.length > 0;

        if (hasStructuralState && nextMessage.content.length < trackedText.content.length) {
          streamedTextByMessageIdRef.current.set(nextMessage.id, {
            content: nextMessage.content,
            sequence: 0,
            epoch: trackedText.epoch + 1,
          });
        } else {
          nextMessage = applyTrackedStreamingContent(
            nextMessage,
            streamedTextByMessageIdRef.current
          );
        }
      } else if (
        nextMessage.content.length === 0 &&
        (nextMessage.toolCalls.length > 0 || nextMessage.processSteps.length > 0)
      ) {
        streamedTextByMessageIdRef.current.set(nextMessage.id, {
          content: '',
          sequence: 0,
          epoch: 1,
        });
      }

      const previewMessage = getConversationPreviewMessage([nextMessage]);

      writeMessages(client, conversationId, [nextMessage]);
      writeConversationPreview(client, { id: conversationId }, previewMessage);
    },
    skip: !conversationId,
  });

  useSubscription(MessageTextDeltaStreamDocument, {
    variables: { conversationId: conversationId ?? '' },
    onData: ({ client, data: subscription }) => {
      if (!subscription.data || subscription.loading || subscription.error || !conversationId) {
        return;
      }

      writeMessageTextDelta({
        cache: client,
        conversationId,
        delta: subscription.data.messageTextDeltaStream,
        streamedTextByMessageId: streamedTextByMessageIdRef.current,
      });
    },
    skip: !conversationId,
  });

  const messages = useMemo(() => {
    const serverMessages = (data?.messages ?? [])
      .map((message) => toCachedMessage(message))
      .map((message) => applyTrackedStreamingContent(message, streamedTextByMessageIdRef.current));
    const seededMessages = initialMessages?.map((message) => toCachedMessage(message)) ?? [];

    return mergeMessages(seededMessages, serverMessages).sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }, [data?.messages, initialMessages]);

  useEffect(() => {
    streamedTextByMessageIdRef.current.clear();
  }, [conversationId]);

  const latestAssistantMessage = useMemo(() => {
    return [...messages].reverse().find((message) => message.sender === SenderType.BOT) ?? null;
  }, [messages]);

  useEffect(() => {
    if (
      !conversationId ||
      !latestAssistantMessage ||
      latestAssistantMessage.status !== MessageStatus.IN_PROGRESS
    ) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void refetch({ conversationId });
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [conversationId, latestAssistantMessage, refetch]);

  const handleSendMessage = ({
    conversationId: inputConversationId,
    content,
    tool,
    projectId,
    reasoningEffort,
    permissionMode,
    requestContext,
    attachmentIds,
  }: SendChatMessageInput) => {
    return sendMessage({
      variables: {
        conversationId: inputConversationId ?? conversationId,
        content,
        tool,
        projectId,
        reasoningEffort,
        permissionMode,
        requestContext,
        attachmentIds,
      },
    });
  };

  return {
    messages,
    messagesLoading: loading,
    sendMessage: handleSendMessage,
    sendMessageLoading,
    sendMessageFeedback,
    sendMessageFeedbackLoading,
    respondToChatToolApproval,
    respondToChatToolApprovalLoading,
  };
};
